import { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash-es/isEqual';
import isPlainObject from 'lodash-es/isPlainObject';
import { ReactComponent as DeleteIcon } from 'assets/iconButtons/delete-new-icon.svg';
import i18nContext from 'components/i18n-context';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { Toggle } from 'uikit/Toggle/Toggle';
import {
  getEmptyValue,
  getEnumLabel,
  getFieldLabel,
  getMaxLength,
  getSchemaType,
  isEnumArraySchema,
  isMapSchema,
  isStringListSchema,
  isSupportedSchema,
  joinPath,
  SCHEMA_TYPE,
  toFullMatchPattern,
  validateBySchema
} from './schemaUtils';

const INTEGER_PATTERN = /^-?\d+$/;
const NUMBER_PATTERN = /^-?\d+(\.\d+)?$/;

// Per-form settings: input names (and ids) are prefixed so two forms on one page never share an id,
// and a read-only form hides the controls that add or remove items
export const SchemaFormContext = createContext({ idPrefix: 'schema-form', isReadOnly: false });

const useInputName = () => {
  const { idPrefix } = useContext(SchemaFormContext);
  return (path) => `${idPrefix}-${path || 'root'}`;
};

const getEnumOptions = (values) => values.map((value) => ({ key: value, value: getEnumLabel(value) }));

const setObjectProperty = (object, key, value) => {
  const result = { ...(object || {}) };
  if (value === undefined) {
    delete result[key];
  } else {
    result[key] = value;
  }
  return result;
};

const COMPACT_JSON_MAX_LENGTH = 80;

// Short values stay on one line, so a map of small objects does not turn into a column of tall text areas
const toJsonText = (value) => {
  if (value === undefined) return '';
  const compactText = JSON.stringify(value);
  return compactText.length <= COMPACT_JSON_MAX_LENGTH ? compactText : JSON.stringify(value, null, 2);
};

// Without a value schema a new map entry copies the shape of an existing one with its values cleared
const getEmptyValueLike = (sample) => {
  if (Array.isArray(sample)) return [];
  if (isPlainObject(sample)) {
    return Object.fromEntries(Object.entries(sample).map(([key, value]) => [key, getEmptyValueLike(value)]));
  }
  if (typeof sample === 'string') return '';
  if (typeof sample === 'boolean') return false;
  return null;
};

const splitTokens = (text) =>
  text
    .split(/[\s,;]+/)
    .map((token) => token.trim())
    .filter(Boolean);

// Upper-cases a token only when that alone makes it match the pattern ("lt" for "[A-Z]{2}")
const normalizeToken = (token, pattern) => {
  if (!pattern) return token;
  const regExp = new RegExp(toFullMatchPattern(pattern), 'u');
  return !regExp.test(token) && regExp.test(token.toUpperCase()) ? token.toUpperCase() : token;
};

// Nothing inside a JSON-edited value is rendered as a field, so an error of a nested path is shown on the value itself
const getOwnOrNestedError = (errors, path) => {
  if (errors[path]) return errors[path];

  const nested = Object.entries(errors).find(
    ([errorPath]) => !path || errorPath.startsWith(`${path}.`) || errorPath.startsWith(`${path}[`)
  );
  if (!nested) return undefined;

  const [errorPath, message] = nested;
  return `${errorPath.slice(path.length).replace(/^\./, '')}: ${message}`;
};

const FieldError = ({ message }) => (message ? <p className={'schema-form-error'}>{message}</p> : null);

FieldError.propTypes = {
  message: PropTypes.string
};

const GroupHeader = ({ label, isRequired, description, error, count }) => (
  <>
    {label && (
      <h4 className={'schema-form-group-title'}>
        {label}
        {isRequired && '*'}
        {count > 0 && <span className={'schema-form-count'}>{count}</span>}
      </h4>
    )}
    {description && <p className={'schema-form-hint'}>{description}</p>}
    <FieldError message={error} />
  </>
);

GroupHeader.propTypes = {
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  description: PropTypes.string,
  error: PropTypes.string,
  count: PropTypes.number
};

const RemoveButton = ({ onClick, isDisabled }) => {
  const i18n = useContext(i18nContext);
  const { isReadOnly } = useContext(SchemaFormContext);

  if (isReadOnly) return null;

  return (
    <button
      type={'button'}
      className={'schema-form-remove-button'}
      onClick={onClick}
      disabled={isDisabled}
      aria-label={i18n.getMessage('integrationSettings.form.button.remove')}
      title={i18n.getMessage('integrationSettings.form.button.remove')}
    >
      <DeleteIcon />
    </button>
  );
};

const AddButton = ({ onClick, isDisabled }) => {
  const i18n = useContext(i18nContext);
  const { isReadOnly } = useContext(SchemaFormContext);

  if (isReadOnly) return null;

  return (
    <Button
      className={'schema-form-add-button'}
      type={'outline'}
      size={'small'}
      onClick={onClick}
      isDisabled={isDisabled}
    >
      {i18n.getMessage('integrationSettings.form.button.add')}
    </Button>
  );
};

RemoveButton.propTypes = {
  onClick: PropTypes.func,
  isDisabled: PropTypes.bool
};

AddButton.propTypes = {
  onClick: PropTypes.func,
  isDisabled: PropTypes.bool
};

const TextField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled }) => {
  const getInputName = useInputName();

  return (
    <Input
      className={'schema-form-input'}
      name={getInputName(path)}
      label={label}
      isRequired={isRequired}
      value={value ?? ''}
      onChange={({ target }) => onChange(target.value)}
      error={errors[path]}
      subText={schema.description}
      max={schema.maxLength}
      isDisabled={isDisabled}
    />
  );
};

// Keeps an incomplete number ("1.", "-") as text so the operator can finish typing; validation flags it meanwhile
const NumberField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled }) => {
  const getInputName = useInputName();
  const pattern = getSchemaType(schema) === SCHEMA_TYPE.INTEGER ? INTEGER_PATTERN : NUMBER_PATTERN;

  const handleChange = ({ target }) => {
    const text = target.value.trim();
    if (!text) {
      onChange(undefined);
    } else {
      onChange(pattern.test(text) ? Number(text) : text);
    }
  };

  return (
    <Input
      className={'schema-form-input schema-form-input-number'}
      name={getInputName(path)}
      label={label}
      isRequired={isRequired}
      value={value === undefined || value === null ? '' : String(value)}
      onChange={handleChange}
      error={errors[path]}
      subText={schema.description}
      isDisabled={isDisabled}
    />
  );
};

const BooleanField = ({ schema, value, path, label, errors, onChange, isDisabled }) => (
  <div className={'schema-form-boolean'}>
    <Toggle isEnabled={Boolean(value)} onChange={onChange} disabled={isDisabled} label={label} />
    {schema.description && <p className={'schema-form-hint'}>{schema.description}</p>}
    <FieldError message={errors[path]} />
  </div>
);

const EnumField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled }) => {
  const getInputName = useInputName();

  return (
    <InputDropDown
      className={'schema-form-input'}
      name={getInputName(path)}
      label={label}
      isRequired={isRequired}
      options={getEnumOptions(schema.enum)}
      value={value}
      onChange={(name, selectedValue) => onChange(selectedValue)}
      error={errors[path]}
      subText={schema.description}
      isDisabled={isDisabled}
    />
  );
};

const EnumArrayField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled }) => {
  const getInputName = useInputName();

  return (
    <InputDropDown
      className={'schema-form-input'}
      name={getInputName(path)}
      label={label}
      isRequired={isRequired}
      options={getEnumOptions(schema.items.enum)}
      value={Array.isArray(value) ? value : []}
      onChange={(name, selectedValues) => onChange(selectedValues)}
      error={errors[path]}
      subText={schema.description}
      isMulti
      isDisabled={isDisabled}
    />
  );
};

const ObjectField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled, isInline }) => {
  const required = schema.required || [];
  // Every top-level field of the setting is a section of its own
  const isRoot = !path && !isInline;

  return (
    <div className={`schema-form-object ${isInline ? 'schema-form-object-inline' : ''}`}>
      {isInline ? (
        <FieldError message={errors[path]} />
      ) : (
        <GroupHeader label={label} isRequired={isRequired} description={schema.description} error={errors[path]} />
      )}
      <div className={`schema-form-object-fields ${isRoot ? 'schema-form-sections' : ''}`}>
        {Object.entries(schema.properties || {}).map(([key, propertySchema]) => {
          const field = (
            <SchemaField
              key={key}
              schema={propertySchema}
              value={value?.[key]}
              path={joinPath(path, key)}
              label={getFieldLabel(propertySchema, key)}
              isRequired={required.includes(key)}
              errors={errors}
              onChange={(propertyValue) => onChange(setObjectProperty(value, key, propertyValue))}
              isDisabled={isDisabled}
            />
          );
          return isRoot ? (
            <section key={key} className={'schema-form-section'}>
              {field}
            </section>
          ) : (
            field
          );
        })}
      </div>
    </div>
  );
};

// A list of short strings (country codes, currencies) as chips; a pasted list is split into items
const StringListField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled }) => {
  const getInputName = useInputName();
  const i18n = useContext(i18nContext);
  const { isReadOnly } = useContext(SchemaFormContext);
  const [text, setText] = useState('');
  const items = Array.isArray(value) ? value : [];
  const isEditable = !isDisabled && !isReadOnly;

  const itemErrors = items.map((item, index) => errors[joinPath(path, index)]);
  const firstErrorIndex = itemErrors.findIndex(Boolean);
  const error =
    errors[path] || (firstErrorIndex >= 0 ? `${items[firstErrorIndex]}: ${itemErrors[firstErrorIndex]}` : undefined);

  const isValidItem = (item) => Object.keys(validateBySchema(schema.items, item)).length === 0;

  // Only valid items become chips; the rest stays in the input to be fixed ("L" on Enter is kept as typed)
  const addItems = (rawText) => {
    const tokens = splitTokens(rawText).map((token) => normalizeToken(token, schema.items.pattern));
    const validTokens = tokens.filter(isValidItem);
    const newItems = validTokens.filter(
      (token, index) => !items.includes(token) && validTokens.indexOf(token) === index
    );
    if (newItems.length) {
      onChange([...items, ...newItems]);
    }
    setText(tokens.filter((token) => !isValidItem(token)).join(' '));
  };
  const removeItem = (index) => onChange(items.filter((item, i) => i !== index));

  const handleKeyDown = (event) => {
    if (['Enter', ',', ';', ' '].includes(event.key)) {
      event.preventDefault();
      addItems(text);
    } else if (event.key === 'Backspace' && !text && items.length) {
      removeItem(items.length - 1);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    addItems(`${text} ${event.clipboardData.getData('text')}`);
  };

  return (
    <div className={'schema-form-array'}>
      <GroupHeader label={label} isRequired={isRequired} description={schema.description} count={items.length} />
      <label className={`schema-form-tags ${error ? 'error' : ''} ${isEditable ? '' : 'disabled'}`}>
        {items.map((item, index) => (
          <span key={item} className={`schema-form-tag ${itemErrors[index] ? 'error' : ''}`} title={itemErrors[index]}>
            {item}
            {isEditable && (
              <button
                type={'button'}
                className={'schema-form-tag-remove'}
                onClick={() => removeItem(index)}
                aria-label={`${i18n.getMessage('integrationSettings.form.button.remove')} ${item}`}
              >
                ×
              </button>
            )}
          </span>
        ))}
        {isEditable && (
          <input
            className={'schema-form-tags-input'}
            name={getInputName(path)}
            value={text}
            maxLength={getMaxLength(schema.items)}
            placeholder={i18n.getMessage('integrationSettings.form.tagsPlaceholder')}
            onChange={({ target }) => setText(target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onBlur={() => addItems(text)}
          />
        )}
        {!isEditable && items.length === 0 && (
          <span className={'schema-form-empty'}>{i18n.getMessage('integrationSettings.form.noItems')}</span>
        )}
      </label>
      <div className={'schema-form-tags-footer'}>
        <FieldError message={error} />
        {isEditable && items.length > 0 && (
          <button type={'button'} className={'schema-form-link-button'} onClick={() => onChange([])}>
            {i18n.getMessage('integrationSettings.form.button.clearAll')}
          </button>
        )}
      </div>
    </div>
  );
};

const ArrayField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled }) => {
  const i18n = useContext(i18nContext);
  const items = Array.isArray(value) ? value : [];
  const isMaxItemsReached = schema.maxItems !== undefined && items.length >= schema.maxItems;

  const updateItem = (index, itemValue) => onChange(items.map((item, i) => (i === index ? itemValue : item)));
  const removeItem = (index) => onChange(items.filter((item, i) => i !== index));
  const addItem = () => onChange([...items, getEmptyValue(schema.items)]);

  return (
    <div className={'schema-form-array'}>
      <GroupHeader label={label} isRequired={isRequired} description={schema.description} error={errors[path]} />
      {items.length === 0 && (
        <p className={'schema-form-empty'}>{i18n.getMessage('integrationSettings.form.noItems')}</p>
      )}
      {items.map((item, index) => (
        <div className={'schema-form-row'} key={index}>
          <div className={'schema-form-row-content'}>
            <SchemaField
              schema={schema.items}
              value={item}
              path={joinPath(path, index)}
              errors={errors}
              onChange={(itemValue) => updateItem(index, itemValue)}
              isDisabled={isDisabled}
              isInline
            />
          </div>
          <RemoveButton onClick={() => removeItem(index)} isDisabled={isDisabled} />
        </div>
      ))}
      <AddButton onClick={addItem} isDisabled={isDisabled || isMaxItemsReached} />
    </div>
  );
};

const MapField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled }) => {
  const getInputName = useInputName();
  const i18n = useContext(i18nContext);
  const { isReadOnly } = useContext(SchemaFormContext);
  const entries = Object.entries(value || {});
  const usedKeys = entries.map(([key]) => key);
  const allowedKeys = schema.propertyNames?.enum;
  const freeKeys = allowedKeys ? allowedKeys.filter((key) => !usedKeys.includes(key)) : [];
  const hasValueSchema = isPlainObject(schema.additionalProperties);

  const renameKey = (oldKey, newKey) => {
    // Two entries cannot share a key; the rename waits until the operator types a free one
    if (newKey === oldKey || usedKeys.includes(newKey)) return;
    onChange(Object.fromEntries(entries.map(([key, entryValue]) => [key === oldKey ? newKey : key, entryValue])));
  };
  const updateEntry = (key, entryValue) => onChange(setObjectProperty(value, key, entryValue));
  const removeEntry = (key) => onChange(Object.fromEntries(entries.filter(([entryKey]) => entryKey !== key)));
  // Without a value schema or a sibling to copy, null is offered: unlike undefined it survives JSON and gets validated
  const getNewEntryValue = () => {
    if (hasValueSchema) return getEmptyValue(schema.additionalProperties);
    return entries.length ? getEmptyValueLike(entries[0][1]) : null;
  };
  const addEntry = (key) => onChange({ ...(value || {}), [key]: getNewEntryValue() });

  return (
    <div className={'schema-form-array'}>
      <GroupHeader
        label={label}
        isRequired={isRequired}
        description={schema.description}
        error={errors[path]}
        count={entries.length}
      />
      {!hasValueSchema && entries.length > 0 && (
        <p className={'schema-form-hint'}>{i18n.getMessage('integrationSettings.form.jsonValuesHint')}</p>
      )}
      {entries.length === 0 ? (
        <p className={'schema-form-empty'}>{i18n.getMessage('integrationSettings.form.noItems')}</p>
      ) : (
        <div className={`schema-form-map ${hasValueSchema ? '' : 'schema-form-map-json'}`}>
          {entries.map(([key, entryValue], index) => (
            <div className={'schema-form-map-row'} key={allowedKeys ? key : index}>
              <div className={'schema-form-map-key'}>
                {allowedKeys ? (
                  <span className={'schema-form-key'}>{getEnumLabel(key)}</span>
                ) : (
                  <Input
                    className={'schema-form-input'}
                    name={getInputName(joinPath(path, `key${index}`))}
                    label={i18n.getMessage('integrationSettings.form.mapKey')}
                    isRequired
                    value={key}
                    onChange={({ target }) => renameKey(key, target.value)}
                    isDisabled={isDisabled}
                  />
                )}
              </div>
              <div className={'schema-form-map-value'}>
                <SchemaField
                  schema={schema.additionalProperties}
                  value={entryValue}
                  path={joinPath(path, key)}
                  errors={errors}
                  onChange={(newEntryValue) => updateEntry(key, newEntryValue)}
                  isDisabled={isDisabled}
                  isInline
                  isCompact
                />
              </div>
              <RemoveButton onClick={() => removeEntry(key)} isDisabled={isDisabled} />
            </div>
          ))}
        </div>
      )}
      {!allowedKeys && <AddButton onClick={() => addEntry('')} isDisabled={isDisabled || usedKeys.includes('')} />}
      {allowedKeys && !isReadOnly && freeKeys.length > 0 && (
        <div className={'schema-form-map-add'}>
          <span className={'schema-form-hint'}>{i18n.getMessage('integrationSettings.form.addKey')}</span>
          {freeKeys.map((key) => (
            <button
              key={key}
              type={'button'}
              className={'schema-form-add-chip'}
              onClick={() => addEntry(key)}
              disabled={isDisabled}
            >
              + {getEnumLabel(key)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Fallback for schema constructs the form does not render: the value is edited as raw JSON
const JsonField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled, isCompact }) => {
  const getInputName = useInputName();
  const i18n = useContext(i18nContext);
  const [text, setText] = useState(() => toJsonText(value));
  const [parseError, setParseError] = useState(null);

  useEffect(() => {
    // Replace the text only when the value changed from outside (discard, reload), not while the operator types
    setText((currentText) => {
      try {
        if (isEqual(JSON.parse(currentText), value)) return currentText;
      } catch (e) {
        // the current text is not valid JSON, so it does not represent the new value
      }
      return toJsonText(value);
    });
    setParseError(null);
  }, [value]);

  const handleChange = ({ target }) => {
    setText(target.value);
    if (!target.value.trim()) {
      setParseError(null);
      onChange(undefined);
      return;
    }
    try {
      const parsedValue = JSON.parse(target.value);
      setParseError(null);
      onChange(parsedValue);
    } catch (e) {
      setParseError(i18n.getMessage('integrationSettings.form.error.invalidJson'));
    }
  };

  // A compact field grows with its text instead of reserving the height of a full JSON document
  const rows = isCompact ? Math.min(8, text.split('\n').length) : 8;
  const hint = isCompact
    ? schema?.description
    : schema?.description || i18n.getMessage('integrationSettings.form.jsonHint');

  return (
    <Input
      className={`schema-form-input schema-form-json ${isCompact ? 'schema-form-json-compact' : ''}`}
      type={'textarea'}
      rows={rows}
      name={getInputName(path)}
      label={label}
      isRequired={isRequired}
      value={text}
      onChange={handleChange}
      error={parseError || getOwnOrNestedError(errors, path)}
      subText={hint}
      isDisabled={isDisabled}
    />
  );
};

const SchemaField = (props) => {
  const { schema } = props;

  if (!isSupportedSchema(schema)) {
    return <JsonField {...props} />;
  }

  const type = getSchemaType(schema);
  if (type === SCHEMA_TYPE.OBJECT) {
    return isMapSchema(schema) ? <MapField {...props} /> : <ObjectField {...props} />;
  }
  if (type === SCHEMA_TYPE.ARRAY) {
    if (isEnumArraySchema(schema)) return <EnumArrayField {...props} />;
    return isStringListSchema(schema) ? <StringListField {...props} /> : <ArrayField {...props} />;
  }
  if (type === SCHEMA_TYPE.BOOLEAN) {
    return <BooleanField {...props} />;
  }
  if (type === SCHEMA_TYPE.INTEGER || type === SCHEMA_TYPE.NUMBER) {
    return <NumberField {...props} />;
  }
  return schema.enum ? <EnumField {...props} /> : <TextField {...props} />;
};

const fieldPropTypes = {
  schema: PropTypes.object,
  value: PropTypes.any,
  path: PropTypes.string,
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  errors: PropTypes.object,
  onChange: PropTypes.func,
  isDisabled: PropTypes.bool,
  isInline: PropTypes.bool,
  isCompact: PropTypes.bool
};

TextField.propTypes = fieldPropTypes;
NumberField.propTypes = fieldPropTypes;
BooleanField.propTypes = fieldPropTypes;
EnumField.propTypes = fieldPropTypes;
EnumArrayField.propTypes = fieldPropTypes;
ObjectField.propTypes = fieldPropTypes;
ArrayField.propTypes = fieldPropTypes;
StringListField.propTypes = fieldPropTypes;
MapField.propTypes = fieldPropTypes;
JsonField.propTypes = fieldPropTypes;
SchemaField.propTypes = fieldPropTypes;

export default SchemaField;
